import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import Footer from '../../Footer/Footer';
import styled from 'styled-components';
import ImageSlantPricing from '../../common/image-slant-pricing';
import WhatsOn from '../../common/What-on';
import OpeningTimes from '../../common/Opening-times';
import ParallaxBanner from '../../common/Parralax-banner';
import { processEvents, GET_EVENTS_QUERY } from '../../../utils/eventUtils';
import { Link } from 'react-router-dom';

import castleImage from '../../../assets/castle2.png';
import gardensImage from '../../../assets/gardens.png';
import retailImage from '../../../assets/retail.png';
import playgroundImage from '../../../assets/play.png';
import goatImage from '../../../assets/goat.webp';
const GET_TICKET_PRICING = gql`
  query GetTicketPricing {
    siteSettings(where: {title: "Global Site Settings"}) {
      nodes {
        siteSettingsFields {
          ticketPricing {
            castleGardensPricing {
              adultType
              adultOnlinePrice
              adultOfficePrice
              childType
              childOnlinePrice
              childOfficePrice
              concessionType
              concessionOnlinePrice
              concessionOfficePrice
              familyType
              familyOnlinePrice
              familyOfficePrice
            }
            gardensPricing {
              adultType
              adultOnlinePrice
              adultOfficePrice
              childType
              childOnlinePrice
              childOfficePrice
              concessionType
              concessionOnlinePrice
              concessionOfficePrice
            }
          }
        }
      }
    }
  }
`;

// Simple query to explore the schema
const GET_SITE_SETTINGS = gql`
  query GetSiteSettings {
    siteSettings {
      nodes {
        title
        siteSettingsFields {
          openingHours {
            castle {
              days
              times
              note
            }
            gardens {
              days
              times
              note
            }
            retail {
              days
              times
              note
            }
          }
        }
      }
    }
  }
`;

const DayTicketsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const PackageSection = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  padding: 4rem 2rem;
  margin: 4rem 0;
  text-align: center;
`;

const PackageContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PackageTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
`;

const PackageDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
`;

const ExtrasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ExtraItem = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ExtraTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const ExtraDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
`;

// Styled note for ticket types
const TicketTypeNote = styled.div`
  background: ${({ theme }) => theme.colors.greyBackground || '#f9f9f9'};
  border: 1.5px solid ${({ theme }) => theme.colors.accent || '#e0e0e0'};
  border-radius: 10px;
  padding: 1.3rem 1.5rem;
  margin: 2.5rem auto 2rem;
  max-width: 600px;
  text-align: center;
  font-size: 1.08rem;
  color: ${({ theme }) => theme.colors.primary || '#444'};
  font-family: ${({ theme }) => theme.fonts.main};
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  & .icon {
    font-size: 1.6rem;
    margin-top: 2px;
  }
  & strong {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 600;
  }
`;

const DayTickets = () => {
  const navigate = useNavigate();
  const { loading: eventsLoading, error: eventsError, data: eventsData } = useQuery(GET_EVENTS_QUERY);
  const { loading: pricingLoading, error: pricingError, data: pricingData } = useQuery(GET_TICKET_PRICING);
  const { loading: settingsLoading, error: settingsError, data: settingsData } = useQuery(GET_SITE_SETTINGS);


  // Default pricing data (fallback if API fails)
  const defaultCastleAndGardensPricing = [
    {
      type: 'Adult',
      onlinePrice: '£23.40',
      officePrice: '£29.99'
    },
    {
      type: 'Child',
      onlinePrice: '£9.00',
      officePrice: '£10.00'
    },
    {
      type: 'Concession',
      onlinePrice: '£21.60',
      officePrice: '£24.00'
    },
    {
      type: 'Family',
      onlinePrice: '£70.20',
      officePrice: '£77.80'
    }
  ];

  const defaultGardensAndPlaygroundPricing = [
    {
      type: 'Adult',
      onlinePrice: '£15.00',
      officePrice: '£18.00'
    },
    {
      type: 'Child',
      onlinePrice: '£7.00',
      officePrice: '£8.00'
    },
    {
      type: 'Concession',
      onlinePrice: '£13.00',
      officePrice: '£15.00'
    }
  ];

  const defaultAnimalParkPricing = [
    {
      type: 'Adult',
      onlinePrice: '£4.50',
      officePrice: '£4.50'
    },
    {
      type: 'Child',
      onlinePrice: '£4.50',
      officePrice: '£4.50'
    },
    {
      type: 'Under 3',
      onlinePrice: 'Free',
      officePrice: 'Free'
    }
  ];


  // Extract opening hours with fallback
  const openingHours = settingsData?.siteSettings?.nodes?.[0]?.siteSettingsFields?.openingHours || {
    column1: {
      title: "Castle",
      days: "Mon - Sun",
      times: "10:00 - 17:00",
      note: "Last entry at 16:00"
    },
    column2: {
      title: "Gardens & Playground",
      days: "Mon - Sun",
      times: "09:00 - 17:00",
      note: "Last entry at 16:30"
    },
    column3: {
      title: "Retail Village",
      days: "Mon - Sun",
      times: "9:00 - 17:00",
      note: ""
    }
  };

  // Format the data for the OpeningTimes component
  const formattedOpeningHours = {
    column1: {
      title: "Castle",
      days: openingHours.castle?.days || "Mon, Thur - Sun",
      times: openingHours.castle?.times || "10:00 - 16:00",
      note: openingHours.castle?.note || "Closed Tuesday and Wednesday"
    },
    column2: {
      title: "Gardens & Playground",
      days: openingHours.gardens?.days || "Mon - Sun",
      times: openingHours.gardens?.times || "09:00 - 17:00",
      note: openingHours.gardens?.note || "Last entry at 16:30"
    },
    column3: {
      title: "Retail Village",
      days: openingHours.retail?.days || "Mon - Sun",
      times: openingHours.retail?.times || "9:00 - 17:00",
      note: openingHours.retail?.note || ""
    }
  };

  // Also update the pricing data extraction
  const pricingSettings = pricingData?.siteSettings?.nodes?.[0]?.siteSettingsFields || {};
  const ticketPricing = pricingSettings.ticketPricing || {};

  // Process castle and gardens pricing
  const cgPricing = ticketPricing.castleGardensPricing || {};
  const castleAndGardensPricing = cgPricing ? [
    {
      type: cgPricing.adultType || 'Adult',
      onlinePrice: cgPricing.adultOnlinePrice || '£23.40',
      officePrice: cgPricing.adultOfficePrice || '£26.00'
    },
    {
      type: cgPricing.childType || 'Child',
      onlinePrice: cgPricing.childOnlinePrice || '£9.00',
      officePrice: cgPricing.childOfficePrice || '£10.00'
    },
    {
      type: cgPricing.concessionType || 'Concession',
      onlinePrice: cgPricing.concessionOnlinePrice || '£21.60',
      officePrice: cgPricing.concessionOfficePrice || '£24.00'
    },
    {
      type: cgPricing.familyType || 'Family',
      onlinePrice: cgPricing.familyOnlinePrice || '£70.20',
      officePrice: cgPricing.familyOfficePrice || '£77.80'
    }
  ] : defaultCastleAndGardensPricing;

  // Process gardens pricing
  const gPricing = ticketPricing.gardensPricing || {};
  const gardensAndPlaygroundPricing = gPricing ? [
    {
      type: gPricing.adultType || 'Adult',
      onlinePrice: gPricing.adultOnlinePrice || '£9.00',
      officePrice: gPricing.adultOfficePrice || '£10.00'
    },
    {
      type: gPricing.childType || 'Child',
      onlinePrice: gPricing.childOnlinePrice || '£5.40',
      officePrice: gPricing.childOfficePrice || '£6.00'
    },
    {
      type: gPricing.concessionType || 'Concession',
      onlinePrice: gPricing.concessionOnlinePrice || '£7.20',
      officePrice: gPricing.concessionOfficePrice || '£8.00'
    }
  ] : defaultGardensAndPlaygroundPricing;

  const handleBooking = (ticketType) => {
    if (ticketType === 'gardens') {
      window.location.href = '/booking-journey?locationId=978&pageId=40ca044f-534f-4b7f-83ea-b4156f0633d3';
      return;
    }

    // All other bookings now go to the new booking journey
    window.location.href = '/booking-journey?locationId=978&pageId=b0f1a62a-a34a-4c4e-a88d-6fda03a19279';
  };

  const handleEventClick = (eventId) => {
    navigate(`/event/${eventId}`);
  };

  const venues = [
    {
      name: "Castle",
      hours: "10:00 - 16:00"
    },
    {
      name: "Gardens & Playground",
      hours: "09:00 - 17:00"
    },
    {
      name: "Retail Village",
      hours: "9:00 - 17:00"
    }
  ];

  // Custom images for this page (using castle2.png instead of castle.png)
  const customImages = {
    castle: castleImage,
    gardens: gardensImage,
    retail: retailImage,
    playground: playgroundImage
  };

  // Get all events
  const eventsContent = eventsLoading ? [] : 
    eventsData && eventsData.events && eventsData.events.nodes ? 
    processEvents(eventsData.events.nodes, customImages) : [];
  
  // Filter for tours based on the eventType field
  const toursContent = eventsContent.filter(event => 
    event.eventType === 'tour'
  );

  if (pricingLoading) return <p>Loading pricing information...</p>;

  return (
    <DayTicketsContainer>
      <ParallaxBanner 
        title="Day Tickets"
        subtitle="Experience the magic of Belvoir Castle"
        image={castleImage}
      />
      <ImageSlantPricing
        image={castleImage}
        title="Castle, Gardens & Playground"
        description="Experience the majesty of our historic castle and beautiful gardens"
        buttonText="Book Now"
        buttonAction={() => window.location.href = '/booking-journey?locationId=978&pageId=b0f1a62a-a34a-4c4e-a88d-6fda03a19279'}
        imagePosition="right"
        pricingData={castleAndGardensPricing}
      />
      <ImageSlantPricing
        image={gardensImage}
        title="Gardens & Playground"
        description="Explore our beautiful gardens and let the children enjoy our adventure playground"
        buttonText="Book Now"
        buttonAction={() => handleBooking('gardens')}
        imagePosition="left"
        pricingData={gardensAndPlaygroundPricing}
      />
      <ImageSlantPricing
        image={goatImage}
        title="Animal Park"
        description="Meet our friendly animals and enjoy a fun-filled day at the Belvoir Animal Park"
        buttonText="Book Now"
        buttonAction={() => handleBooking('animal-park')}
        imagePosition="left"
        pricingData={defaultAnimalParkPricing}
      />
      {/* Ticket type age/concession note */}
      <TicketTypeNote>
        <span className="icon" role="img" aria-label="info">ℹ️</span>
        <span>
          <strong>Ticket Types:</strong> Adult is 18+, Child is 3-17, Under 3 is 0-2 (free), Concession is 65+ and Students (with valid ID).
        </span>
      </TicketTypeNote>
      <PackageSection>
        <PackageContent>
          <PackageTitle>Build Your Perfect Day Out</PackageTitle>
          <PackageDescription>
            When booking your tickets, you'll have the opportunity to enhance your visit with our range of exciting extras. 
            Create your perfect package by adding these memorable experiences to your day.
          </PackageDescription>
          <ExtrasGrid>
            <ExtraItem>
              <ExtraTitle>Afternoon Tea</ExtraTitle>
              <ExtraDescription>
                Indulge in our exquisite Afternoon Tea service in the elegant Aviary Tearoom
              </ExtraDescription>
            </ExtraItem>
            <ExtraItem>
              <ExtraTitle>Guided Tours</ExtraTitle>
              <ExtraDescription>
                Join our expert guides for fascinating insights into the Castle's history
              </ExtraDescription>
            </ExtraItem>
            <ExtraItem>
              <ExtraTitle>Animal Park</ExtraTitle>
              <ExtraDescription>
                Meet our friendly animals at the Belvoir Castle Animal Park
              </ExtraDescription>
            </ExtraItem>
            <ExtraItem>
              <ExtraTitle>Seasonal Activities</ExtraTitle>
              <ExtraDescription>
                Discover special seasonal events and activities available during your visit
              </ExtraDescription>
            </ExtraItem>
          </ExtrasGrid>
        </PackageContent>
      </PackageSection>
      <OpeningTimes columns={formattedOpeningHours} />
      {eventsLoading ? (
        <p>Loading events...</p>
      ) : eventsError ? (
        <p>Error loading events: {eventsError.message}</p>
      ) : (
        <>
          <WhatsOn 
            title="What's On at Belvoir Castle" 
            events={eventsContent} 
            onEventClick={handleEventClick}
          />
          {toursContent.length > 0 && (
            <WhatsOn 
              title="Daily Tours at Belvoir Castle" 
              events={toursContent} 
              onEventClick={handleEventClick}
            />
          )}
        </>
      )}
      <Footer />
    </DayTicketsContainer>
  );
};

export default DayTickets; 